/**
 * v3.15-1162 — Application Concierge (outcome-first, Apple style).
 *
 * Sells peace of mind, not responsibilities. Outcomes lead. Larger typography.
 * Real Hub screenshots dominate.
 */
import PublicLayout from '../components/PublicLayout';
import EndPageCTA from '../components/EndPageCTA';
import {
  HubCommunicationsScreenshot,
  HubDashboardScreenshot,
  HubClientsScreenshot,
} from '../components/HubProductImagery';

const ConciergePage = () => (
  <PublicLayout testId="concierge-page">

    {/* HERO — large statement, dark */}
    <section className="bg-primary text-white px-6 py-32 lg:py-44">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Application Concierge</p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-8">
          You sell.<br />
          <span className="text-gradient-gold">We handle everything else.</span>
        </h1>
        <p className="text-xl sm:text-2xl text-slate-300 font-light max-w-2xl leading-relaxed">
          One dedicated Application Concierge per client. From the moment you make the introduction until the day funds land.
        </p>
      </div>
    </section>

    {/* OUTCOMES — three outcome statements, with proof: real Hub Clients view */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">What changes the day you partner with us</p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-16 items-start">
          <div className="space-y-14">
            <div data-testid="outcome-1">
              <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.1]">
                Your salespeople stop chasing clients.
              </h3>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                Every conversation, every follow-up, every document request — owned by your Concierge. Your team stays selling.
              </p>
            </div>
            <div data-testid="outcome-2">
              <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.1]">
                Your phone stops ringing with status questions.
              </h3>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                Clients get answers from their Concierge directly. You stay above the noise.
              </p>
            </div>
            <div data-testid="outcome-3">
              <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary leading-[1.1]">
                Your deals start closing faster.
              </h3>
              <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                One owner. Zero handoffs. Documents collected on the first ask. Lenders worked in parallel until the right answer surfaces.
              </p>
            </div>
          </div>
          <div className="lg:sticky lg:top-24">
            <HubClientsScreenshot />
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">
              The Concierge works every active file inside the Hub. Your team sees the same view they do — without doing the work.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* CLIENT EXPERIENCE — dark with screenshot */}
    <section className="bg-slate-900 text-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">From your client&rsquo;s side</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-8">
            They never call a 1-800 number.
          </h2>
          <p className="text-lg text-slate-300 max-w-md leading-relaxed">
            They call Maria. Or David. Or whoever owns their file. By name. Every time. From application through funding day.
          </p>
        </div>
        <HubCommunicationsScreenshot />
      </div>
    </section>

    {/* PARTNER VIEW — light with dashboard */}
    <section className="bg-white px-6 py-28 lg:py-36">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-16 items-center">
        <HubDashboardScreenshot />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">From your side</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] mb-8">
            You always know exactly where every file stands.
          </h2>
          <p className="text-lg text-slate-600 max-w-md leading-relaxed">
            Live status. Live activity. Live partner messaging. No status calls. No follow-up emails. Just the dashboard.
          </p>
        </div>
      </div>
    </section>

    {/* CLOSING — large typography */}
    <section className="bg-gradient-to-b from-slate-50 to-white px-6 py-32 lg:py-44">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.1] mb-8">
          One Concierge.<br />
          <span className="text-gradient-gold">Zero handoffs.</span>
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          The Concierge model isn&rsquo;t a feature. It&rsquo;s the entire reason partners trust us with their most valuable clients.
        </p>
      </div>
    </section>

    <EndPageCTA testId="concierge-end-cta" />
  </PublicLayout>
);

export default ConciergePage;
